import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, ConfirmDialog, ErrorState, Input, MoneyInput, PageHeader, PageLoader, PasswordInput, Select, Switch, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection } from '../../../components/common/Parts/Parts';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { PAY_TYPE_OPTIONS } from '../../../constants/payments';
import { ROLE_OPTIONS } from '../../../constants/roles';
import { serverFieldErrors } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { teamService } from '../services/teamService';
import type { Role } from '../../../constants/roles';
import type { PayType, UserInput } from '../types/teamTypes';
import type { ActionProps } from '../../../hooks/useAction';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const ROLE_HELP = {
  owner: 'Everything, including settings and team',
  office: 'Quotations, jobs, bills, payments and stock',
  worker: 'Only their own jobs on the phone',
};

function ResetPassword({ id, run, busy }: ActionProps & { id: string }) {
  const [pw, setPw] = useState('');
  const save = async () => {
    if (await run('reset', () => teamService.resetPassword(id, pw), 'Password changed. They must log in again.')) setPw('');
  };
  return (
    <FormSection title="Reset password" subtitle="Logs them out on every device">
      <PasswordInput label="New password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} hint="At least 8 characters" />
      <div className="flex items-end">
        <Button variant="outline" loading={busy === 'reset'} disabled={pw.length < 8} onClick={save}>Set new password</Button>
      </div>
    </FormSection>
  );
}

export default function TeamForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const { user: me } = useAuth();
  const { busy, run } = useAction(toast);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm({
    name: '', phone: '', email: '', role: 'worker' as Role, password: '', isActive: true, payType: 'daily' as PayType | '', dailyWage: null, monthlySalary: null, overtimeRate: null,
  });
  const { data: u, loading, error, reload } = useFetch(() => (editing ? teamService.get(id) : Promise.resolve(null)), [id]);
  useCrumb(`/team/${id}`, u?.name);
  useEffect(() => {
    if (u) {
      setForm({
        name: u.name, phone: u.phone, email: u.email ?? '', role: u.role, password: '', isActive: u.isActive,
        payType: u.payType ?? '', dailyWage: u.dailyWage || null, monthlySalary: u.monthlySalary || null, overtimeRate: u.overtimeRate || null,
      });
    }
  }, [u, setForm]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !u) return <PageLoader />;
  const self = editing && me?._id === id;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (form.name.trim().length < 2) errs.name = 'Enter the full name';
    if (!/^\d{10}$/.test(form.phone.trim())) errs.phone = 'Enter a 10 digit mobile number (used to log in)';
    if (!editing && form.password.length < 8) errs.password = 'At least 8 characters';
    const onPayroll = form.role !== 'owner' && form.payType;
    if (onPayroll && form.payType === 'daily' && !(form.dailyWage > 0)) errs.dailyWage = 'Enter the daily wage';
    if (onPayroll && form.payType === 'monthly' && !(form.monthlySalary > 0)) errs.monthlySalary = 'Enter the monthly salary';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const pay = !onPayroll ? { payType: null } : { payType: form.payType as PayType, dailyWage: form.dailyWage ?? 0, monthlySalary: form.monthlySalary ?? 0, overtimeRate: form.overtimeRate ?? 0 };
    const body: UserInput = { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(), role: form.role, ...pay };
    setSaving(true);
    try {
      if (id) await teamService.update(id, { ...body, isActive: form.isActive });
      else await teamService.create({ ...body, password: form.password });
      toast.success(editing ? 'Team member updated' : `${body.name} can now log in with ${body.phone}`);
      navigate('/team', { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };
  const remove = async () => {
    if (await run('delete', () => teamService.remove(id), `${u?.name ?? 'Team member'} removed`)) navigate('/team', { replace: true });
    setConfirmDelete(false);
  };

  const actions = (
    <>
      {editing && !self && <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Remove</Button>}
      <Button variant="outline" onClick={() => navigate('/team')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="team-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Add member'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${u?.name ?? 'team member'}` : 'Add team member'} subtitle="They log in with their mobile number and password" actions={actions} />
      <form id="team-form" onSubmit={submit} noValidate className="space-y-5">
        <FormSection title="Details">
          <Input label="Full name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} />
          <Input label="Mobile (login)" required type="tel" inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => set('phone')(e.target.value.replace(/\D/g, ''))} error={errors.phone} />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} placeholder="Optional" />
          <Select label="Role" options={ROLE_OPTIONS.map((o) => ({ ...o, description: ROLE_HELP[o.value] }))} value={form.role} onChange={set('role')} disabled={self} searchable={false} hint={ROLE_HELP[form.role]} />
          {!editing && <PasswordInput label="Password" required autoComplete="new-password" value={form.password} onChange={set('password')} error={errors.password} hint="At least 8 characters. Share it with them." />}
        </FormSection>
        {form.role !== 'owner' && (
          <FormSection title="Pay" subtitle="Used by attendance and salary slips">
            <Select
              label="Paid"
              options={[...PAY_TYPE_OPTIONS, { value: '', label: 'Not on payroll' }]}
              value={form.payType}
              onChange={set('payType')}
              searchable={false}
              hint={form.payType === 'daily' ? 'Paid for days worked: present = 1 day, half day = ½' : form.payType === 'monthly' ? 'Full salary; absent days are deducted' : undefined}
            />
            {form.payType === 'daily' && <MoneyInput label="Daily wage" required value={form.dailyWage} onChange={set('dailyWage')} error={errors.dailyWage} placeholder="0" />}
            {form.payType === 'monthly' && <MoneyInput label="Monthly salary" required value={form.monthlySalary} onChange={set('monthlySalary')} error={errors.monthlySalary} placeholder="0" />}
            {form.payType && (
              <MoneyInput label="Overtime rate per hour" value={form.overtimeRate} onChange={set('overtimeRate')} placeholder="Auto" hint="Leave empty to pay 1/8 of a day's pay per hour" />
            )}
          </FormSection>
        )}
        {editing && !self && (
          <Card className="p-5">
            <Switch label="Can log in" description="Turn off to block this person without deleting them" checked={form.isActive} onChange={set('isActive')} />
          </Card>
        )}
      </form>
      {editing && <div className="mt-5"><ResetPassword id={id} run={run} busy={busy} /></div>}
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={remove} loading={busy === 'delete'} danger title={`Remove ${u?.name}?`} message="They will no longer be able to log in. Their past work stays saved." confirmLabel="Remove" />
    </>
  );
}
